import { Select } from "antd";
import { ColorTheme, getThemeInfo, getThemePreview } from "src/utils/colors-v2";
import { useColorScheme } from "src/hooks/useColorScheme";

export type ColorRampConfig = {
  theme: ColorTheme;
};

function ThemePreview({
  name,
  description,
  colors,
  type,
  showDescription = true,
}: {
  name: string;
  description: string;
  colors: string[];
  type: "categorical" | "sequential" | "diverging";
  showDescription?: boolean;
}) {
  return (
    <div
      style={{
        padding: "0",
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ fontWeight: 500 }}>{name}</div>
        <div
          style={{
            fontSize: "10px",
            padding: "2px 6px",
            borderRadius: "4px",
            backgroundColor:
              type === "categorical"
                ? "#e6f7ff"
                : type === "sequential"
                ? "#f6ffed"
                : "#fff7e6",
            color:
              type === "categorical"
                ? "#1890ff"
                : type === "sequential"
                ? "#52c41a"
                : "#fa8c16",
            marginLeft: 4,
          }}
        >
          {type}
        </div>
        <div style={{ flex: 1, marginLeft: 8 }}>
          <div
            style={{
              height: 16,
              borderRadius: 2,
              background: `linear-gradient(to right, ${colors.join(", ")})`,
            }}
          />
        </div>
      </div>
      {showDescription && (
        <div
          style={{
            fontSize: "12px",
            color: "#666",
            marginTop: 4,
          }}
        >
          {description}
        </div>
      )}
    </div>
  );
}

export function ColorRampPicker({
  ramp,
  onChangeRamp,
}: {
  ramp: ColorTheme;
  onChangeRamp(value: ColorTheme): void;
}) {
  const mode = useColorScheme();

  // Group themes by type for better organization
  const categoricalThemes = [
    ColorTheme.OBSERVABLE_10,
    ColorTheme.OBSERVABLE_20,
    ColorTheme.CORPORATE,
    ColorTheme.MODERN,
    ColorTheme.ACCESSIBLE_PRO,
    ColorTheme.COLORBREWER_SET1,
    ColorTheme.COLORBREWER_SET2,
    ColorTheme.COLORBREWER_PAIRED,
    ColorTheme.COLORBREWER_ACCENT,
    ColorTheme.ACCESSIBLE,
  ];

  const sequentialThemes = [
    ColorTheme.VIRIDIS,
    ColorTheme.PLASMA,
    ColorTheme.BLUES,
    ColorTheme.GREENS,
  ];

  const divergingThemes = [ColorTheme.RED_BLUE, ColorTheme.BROWN_GREEN];

  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginBottom: 12 }}>
        <Select
          value={ramp}
          onChange={onChangeRamp}
          style={{ width: "100%" }}
          bordered={false}
          popupMatchSelectWidth={false}
          className="viz-select"
          labelInValue={false}
        >
          {/* Categorical Themes */}
          <Select.OptGroup label="Categorical (for different categories)">
            {categoricalThemes.map((theme) => {
              const themeInfo = getThemeInfo(theme);
              const previewColors = getThemePreview(theme);
              return (
                <Select.Option
                  key={theme}
                  value={theme}
                  className="viz-select-option"
                >
                  <ThemePreview
                    name={themeInfo.name}
                    description={themeInfo.description}
                    colors={previewColors}
                    type={themeInfo.type}
                  />
                </Select.Option>
              );
            })}
          </Select.OptGroup>

          {/* Sequential Themes */}
          <Select.OptGroup label="Sequential (for ordered data)">
            {sequentialThemes.map((theme) => {
              const themeInfo = getThemeInfo(theme);
              const previewColors = getThemePreview(theme);
              return (
                <Select.Option
                  key={theme}
                  value={theme}
                  className="viz-select-option"
                >
                  <ThemePreview
                    name={themeInfo.name}
                    description={themeInfo.description}
                    colors={previewColors}
                    type={themeInfo.type}
                  />
                </Select.Option>
              );
            })}
          </Select.OptGroup>

          {/* Diverging Themes */}
          <Select.OptGroup label="Diverging (for data with meaningful center)">
            {divergingThemes.map((theme) => {
              const themeInfo = getThemeInfo(theme);
              const previewColors = getThemePreview(theme);
              return (
                <Select.Option
                  key={theme}
                  value={theme}
                  className="viz-select-option"
                >
                  <ThemePreview
                    name={themeInfo.name}
                    description={themeInfo.description}
                    colors={previewColors}
                    type={themeInfo.type}
                  />
                </Select.Option>
              );
            })}
          </Select.OptGroup>
        </Select>
      </div>
    </div>
  );
}

export function toColorRampConfig(ramp: ColorTheme): ColorRampConfig {
  return { theme: ramp };
}
